"use client";

import { useEffect, useRef, useState } from "react";
import { Phone } from "lucide-react";
import { CTA, CONTACT } from "@/config/site";
import { cn } from "@/lib/utils";

export function StickyCtaBar() {
  const [visible, setVisible] = useState(false);
  const contactVisible = useRef(false);

  useEffect(() => {
    const update = () => {
      setVisible(window.scrollY > 500 && !contactVisible.current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });

    const contact = document.querySelector<HTMLElement>("#kontakt");
    let observer: IntersectionObserver | undefined;

    if (contact && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            contactVisible.current = entry.isIntersecting;
          }
          update();
        },
        { threshold: 0.01 },
      );
      observer.observe(contact);
    }

    return () => {
      window.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-line bg-paper px-4 py-3 shadow-[var(--shadow-sticky)] md:hidden",
        "transition-transform duration-[260ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
        visible ? "translate-y-0" : "translate-y-full",
      )}
      inert={visible ? undefined : true}
    >
      <div className="flex items-center gap-3">
        <a
          href={CTA.href}
          tabIndex={visible ? 0 : -1}
          className="inline-flex min-h-13 flex-1 items-center justify-center rounded-xs bg-clay px-6 font-sans text-sm font-medium text-paper active:scale-[0.985] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
        >
          {CTA.shortLabel}
        </a>
        <a
          href={CONTACT.phoneHref}
          tabIndex={visible ? 0 : -1}
          aria-label={`Zavolat ${CONTACT.phone}`}
          className="inline-flex h-13 w-13 shrink-0 items-center justify-center rounded-xs border border-line-strong text-ink active:scale-[0.985] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
        >
          <Phone aria-hidden="true" size={18} strokeWidth={1.5} />
        </a>
      </div>
    </div>
  );
}
